import { useState } from 'react';
import { connect } from 'react-redux';
import { createApp } from '../../../services/app/app-service';
import { createPromptConfigInstructions } from '../../../services/prompt-ai';
import { dataSourceCreate } from '../../../services/knowledge/ds-connector/file-connector';
import { listDataSources } from '../../../services/knowledge';
import { appCreateNotify } from '../../../redux/app/app-actions';
import { INDUSTRIES, AGENT_TYPES, BUILDING_STEPS, TOTAL_STEPS, PROMPT_TEMPLATES } from './consts';
import LogoBox from '../../../assets/images/logo/logo.png';
import { HiCheck, HiChevronRight, HiOutlineChatAlt2 } from 'react-icons/hi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

// Agent Type step (Step 2) is temporarily disabled. When true, the first agent type of the
// selected industry is auto-selected and the flow jumps from Step 1 to Step 3.
// Set to false to bring the Agent Type step back.
const SKIP_AGENT_TYPE_STEP = true;

const mapStateToProps = () => ({});
const mapDispatchToProps = dispatch => ({
    appCreateNotifyHandle: (appId) => dispatch(appCreateNotify(appId)),
});

function OnboardingWizard({ appName: appNameProp, onCancel, appCreateNotifyHandle }) {
    const [currentStep, setCurrentStep] = useState(1);

    // Step 1 data
    const [appName, setAppName] = useState(appNameProp || '');
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [industryDropdownOpen, setIndustryDropdownOpen] = useState(false);

    // Step 2 data
    const [selectedAgentType, setSelectedAgentType] = useState(null);

    // Step 3 data
    const [websiteUrl, setWebsiteUrl] = useState('');

    // Step 4 data
    const [buildingStepIndex, setBuildingStepIndex] = useState(0);
    const [buildProgress, setBuildProgress] = useState(0);
    const [extracting, setExtracting] = useState(false);
    const [extractionReady, setExtractionReady] = useState(false);
    const [extractionFailed, setExtractionFailed] = useState(false);

    // Step 5 data
    const [createdAppId, setCreatedAppId] = useState(null);
    const [agentDisplayName, setAgentDisplayName] = useState('');

    const completionPercent = Math.round(((currentStep - 1) / (TOTAL_STEPS - 1)) * 100);

    const getStepLabel = (step) => {
        const labels = { 2: 'Agent Type', 3: 'Configure', 4: 'Building', 5: 'Complete' };
        return labels[step] || '';
    };

    const selectedIndustryObj = INDUSTRIES.find(i => i.value === selectedIndustry);
    const agentTypes = AGENT_TYPES[selectedIndustry] || [];

    // Step 1 -> Step 2
    const handleGetStarted = () => {
        if (appName.trim() && selectedIndustry) {
            if (SKIP_AGENT_TYPE_STEP) {
                setSelectedAgentType(agentTypes[0] || null);
                setCurrentStep(3);
                return;
            }
            setCurrentStep(2);
        }
    };

    // Step 2 -> Step 3
    const handleContinue = () => {
        if (selectedAgentType) {
            setCurrentStep(3);
        }
    };

    // Step 3 -> Step 4 (build)
    const handleBuildAgent = async () => {
        if (!websiteUrl.trim()) return;

        const agentName = appName;
        setAgentDisplayName(agentName);
        setCurrentStep(4);

        // Simulate building steps
        for (let i = 0; i < BUILDING_STEPS.length; i++) {
            setBuildingStepIndex(i);
            setBuildProgress(Math.round(((i + 1) / BUILDING_STEPS.length) * 100));
            await new Promise(r => setTimeout(r, 1200));
        }

        // Create the app
        const formData = {
            name: agentName,
            description: `${selectedAgentType.description} Industry: ${selectedIndustryObj?.label || ''}. Website: ${websiteUrl}`,
            intentLess: true,
            templateId: null,
            appMode: 'prompt_based',
            storeAddress: websiteUrl,
            nluSettings: { provider: 'OpenAI', apiKey: '' },
        };

        const { response, status } = await createApp(formData);
        if (status === 201) {
            const appId = response.appId;
            setCreatedAppId(appId);
            appCreateNotifyHandle(appId);

            // Save generated prompt
            const template = PROMPT_TEMPLATES[selectedAgentType.id] || PROMPT_TEMPLATES.general_support;
            const promptPayload = {
                globalPrompt: template.globalPrompt
                    .replace(/\{\{APP_NAME\}\}/g, appName)
                    .replace(/\{\{WEBSITE_URL\}\}/g, websiteUrl),
                personality: template.personality,
                tone: template.tone,
            };
            await createPromptConfigInstructions(appId, promptPayload);

            // Create web data source for the website with manual extraction
            const dsId = `${appName.replace(/\s+/g, '-').toLowerCase()}-website`;
            await dataSourceCreate(appId, {
                dataSourceId: dsId,
                dataSourceType: 'web',
                dataType: 'web',
                descriptionForModel: `${appName} company website`,
                urls: [websiteUrl],
                manualExtract: true,
            });

            // Poll listDataSources until modelStatus is "ready" (max 3 min)
            setExtracting(true);
            const MAX_POLL_MS = 3 * 60 * 1000;
            const POLL_INTERVAL = 5000;
            const startTime = Date.now();
            let ready = false;

            while (Date.now() - startTime < MAX_POLL_MS) {
                await new Promise(r => setTimeout(r, POLL_INTERVAL));
                try {
                    const { response: dsListResponse, status: dsListStatus } = await listDataSources(appId);
                    if (dsListStatus === 200) {
                        const dsList = dsListResponse?.data || dsListResponse || [];
                        const ds = dsList.find(d => d.dataSourceId === dsId);
                        if (ds && ds.modelStatus === 'ready') {
                            ready = true;
                            break;
                        }
                    }
                } catch (e) {
                    console.debug('polling listDataSources failed', e);
                }
            }

            setExtracting(false);
            if (ready) {
                setExtractionReady(true);
            } else {
                setExtractionFailed(true);
            }
            // Proceed to step 5 regardless (agent is usable, extraction may still finish)
            setCurrentStep(5);
        } else {
            setCurrentStep(3);
        }
    };

    const handleTestAgent = () => {
        if (createdAppId) {
            window.open(
                `https://web-plugin.botcircuits.com/?app_id=${createdAppId}&test_mode=1&test_voice_mode=1`,
                '_blank'
            );
        }
    };

    const handleExplorePlatform = () => {
        if (createdAppId) {
            // window.location.href = `/app/${createdAppId}/agent`;
            window.location.href = `/app/${createdAppId}/console`;
        }
    };

    // Get sample questions based on agent type
    const getSampleQuestions = () => {
        if (!selectedAgentType) return [];
        const questionMap = {
            payment_collections: [
                'What is my outstanding balance?',
                'Can I set up a payment plan?',
                'I need a payment link for my invoice',
            ],
            customer_onboarding: [
                'What documents do I need to submit?',
                'What is the status of my application?',
                'How do I complete my KYC verification?',
            ],
            customer_support_triage: [
                'I have an issue with my account',
                'Can you help me reset my password?',
                'I need to speak to a specialist',
            ],
            delivery_coordination: [
                'Can I reschedule my delivery to tomorrow?',
                'I need to update my delivery address',
                'What time window is my delivery scheduled for?',
            ],
            shipment_tracking: [
                'Where is my package right now?',
                'My shipment is delayed, what happened?',
                'Can I get a tracking number?',
            ],
            warehouse_support: [
                'Is this item in stock?',
                'When will my order be dispatched?',
                'Can you check inventory for this SKU?',
            ],
            booking_assistant: [
                'I need to book a flight to London',
                'Can I change my hotel reservation?',
                'Show me my upcoming itinerary',
            ],
            travel_support: [
                'Do I need a visa for Thailand?',
                'Are there any travel advisories?',
                'What documents do I need?',
            ],
            customer_experience: [
                'I want to share feedback about my trip',
                'How many loyalty points do I have?',
                'I need a refund for my cancelled booking',
            ],
            appointment_scheduler: [
                'I need to book an appointment',
                'Can I reschedule my visit?',
                'When is my next appointment?',
            ],
            patient_support: [
                'Are my lab results ready?',
                'I need a prescription refill',
                'What are your office hours?',
            ],
            billing_agent: [
                'Can you explain this charge?',
                'Does my insurance cover this?',
                'I need to set up a payment plan',
            ],
            claims_agent: [
                'I need to file a new claim',
                'What is the status of my claim?',
                'What documents do I need to submit?',
            ],
            policy_agent: [
                'When does my policy expire?',
                'What does my plan cover?',
                'I need to update my policy details',
            ],
            quote_agent: [
                'Can I get a quote for auto insurance?',
                'What plans do you recommend?',
                'How much would full coverage cost?',
            ],
            general_support: [
                'I have a question about your service',
                'Can you help me with my account?',
                'I need to speak with someone',
            ],
            sales_agent: [
                'What products do you offer?',
                'Can I get a discount?',
                'I want to place an order',
            ],
            onboarding_agent: [
                'How do I get started?',
                'What features are available?',
                'Can you walk me through the setup?',
            ],
        };
        return questionMap[selectedAgentType.id] || questionMap.general_support;
    };

    return (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                    <img src={LogoBox} alt="BotCircuits" className="h-8 w-8 rounded-lg" />
                    <span className="text-sm font-semibold text-gray-900">BotCircuits</span>
                </div>
                <span className="text-xs font-mono text-gray-500">
                    {currentStep === 4 ? 'Building...' : `${completionPercent}% complete`}
                </span>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
                <div className="max-w-lg mx-auto px-6 py-12">

                    {/* Step 1: Welcome */}
                    {currentStep === 1 && (
                        <div className="text-center">
                            <div className="flex justify-center mb-6">
                                <img src={LogoBox} alt="BotCircuit" className="h-20 w-20 rounded-2xl" />
                            </div>
                            <p className="text-xs font-mono tracking-widest text-gray-400 uppercase mb-3">
                                Welcome to BotCircuits
                            </p>
                            <h1 className="text-3xl font-bold text-gray-900 mb-3">
                                Let's set up your agent 🚀
                            </h1>
                            <p className="text-gray-500 mb-10">
                                Tell us about your application so we can tailor the
                                <br />perfect AI agent for you.
                            </p>

                            <div className="text-left space-y-6">
                                {/* Application Name */}
                                <div>
                                    <label className="block text-xs font-mono tracking-widest text-gray-400 uppercase mb-2">
                                        Application Name
                                    </label>
                                    <input
                                        type="text"
                                        value={appName}
                                        onChange={(e) => setAppName(e.target.value)}
                                        placeholder="Enter your application name"
                                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300"
                                    />
                                </div>

                                {/* Industry Dropdown */}
                                <div className="relative">
                                    <label className="block text-xs font-mono tracking-widest text-gray-400 uppercase mb-2">
                                        What's your industry?
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setIndustryDropdownOpen(!industryDropdownOpen)}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-left flex items-center justify-between focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300"
                                    >
                                        <span className={selectedIndustryObj ? 'text-gray-900' : 'text-gray-400'}>
                                            {selectedIndustryObj
                                                ? `${selectedIndustryObj.icon} ${selectedIndustryObj.label}`
                                                : 'Select your industry'}
                                        </span>
                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {industryDropdownOpen && (
                                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                                            {INDUSTRIES.map((industry) => (
                                                <button
                                                    key={industry.value}
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedIndustry(industry.value);
                                                        setIndustryDropdownOpen(false);
                                                        setSelectedAgentType(null);
                                                    }}
                                                    className="w-full px-4 py-3 text-sm text-left flex items-center gap-2 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                                                >
                                                    {selectedIndustry === industry.value && (
                                                        <HiCheck className="w-4 h-4 text-gray-900" />
                                                    )}
                                                    <span className={selectedIndustry === industry.value ? 'font-medium' : ''}>
                                                        {industry.icon} {industry.label}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button
                                onClick={handleGetStarted}
                                disabled={!appName.trim() || !selectedIndustry}
                                className="mt-10 w-full py-3.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed bg-primary text-white hover:bg-secondary"
                            >
                                Let's get started <HiChevronRight className="w-4 h-4" />
                            </button>

                            <p className="mt-3 text-xs text-gray-400">Takes about 2 minutes</p>
                        </div>
                    )}

                    {/* Step 2: Agent Type (hidden while SKIP_AGENT_TYPE_STEP is true) */}
                    {!SKIP_AGENT_TYPE_STEP && currentStep === 2 && (
                        <div>
                            {/* Progress bars */}
                            <div className="flex gap-2 mb-6">
                                <div className="flex-1 h-0.5 bg-gray-900 rounded" />
                                <div className="flex-1 h-0.5 bg-gray-200 rounded" />
                                <div className="flex-1 h-0.5 bg-gray-200 rounded" />
                                <div className="flex-1 h-0.5 bg-gray-200 rounded" />
                            </div>

                            <div className="flex items-center justify-between mb-8">
                                <p className="text-xs font-mono tracking-widest text-gray-400 uppercase">
                                    Step 2 of {TOTAL_STEPS}
                                </p>
                                <p className="text-xs font-mono text-gray-400">
                                    {getStepLabel(2)}
                                </p>
                            </div>

                            <p className="text-xs font-mono tracking-widest text-gray-400 uppercase mb-3">
                                Choose your agent
                            </p>
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                What kind of agent<br />do you need?
                            </h2>
                            <p className="text-gray-500 mb-8">
                                Pick the agent that best fits your use case.
                            </p>

                            <div className="space-y-3">
                                {agentTypes.map((agent) => (
                                    <button
                                        key={agent.id}
                                        type="button"
                                        onClick={() => setSelectedAgentType(agent)}
                                        className={`w-full p-5 rounded-xl border text-left transition-all flex items-start gap-4 ${
                                            selectedAgentType?.id === agent.id
                                                ? 'border-gray-900 bg-gray-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <span className="text-2xl mt-0.5">{agent.icon}</span>
                                        <div className="flex-1">
                                            <p className="font-semibold text-gray-900">{agent.name}</p>
                                            <p className="text-sm text-gray-500 mt-1">{agent.description}</p>
                                        </div>
                                        {selectedAgentType?.id === agent.id && (
                                            <HiCheck className="w-5 h-5 text-gray-900 mt-1" />
                                        )}
                                    </button>
                                ))}
                            </div>

                            <div className="flex gap-3 mt-8">
                                <button
                                    onClick={() => setCurrentStep(1)}
                                    className="flex-1 py-3.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleContinue}
                                    disabled={!selectedAgentType}
                                    className="flex-1 py-3.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed bg-primary text-white hover:bg-secondary"
                                >
                                    Continue <HiChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Configure */}
                    {currentStep === 3 && (
                        <div>
                            {/* Progress bars */}
                            <div className="flex gap-2 mb-6">
                                <div className="flex-1 h-0.5 bg-gray-900 rounded" />
                                <div className="flex-1 h-0.5 bg-gray-900 rounded" />
                                <div className="flex-1 h-0.5 bg-gray-200 rounded" />
                                <div className="flex-1 h-0.5 bg-gray-200 rounded" />
                            </div>

                            <div className="flex items-center justify-between mb-8">
                                <p className="text-xs font-mono tracking-widest text-gray-400 uppercase">
                                    {/* Step 3 of {TOTAL_STEPS} */}
                                    Step {SKIP_AGENT_TYPE_STEP ? 2 : 3} of {SKIP_AGENT_TYPE_STEP ? TOTAL_STEPS - 1 : TOTAL_STEPS}
                                </p>
                                <p className="text-xs font-mono text-gray-400">
                                    {getStepLabel(3)}
                                </p>
                            </div>

                            <div className="text-center mb-8">
                                <p className="text-xs font-mono tracking-widest text-gray-400 uppercase mb-3">
                                    {selectedAgentType?.name}
                                </p>
                                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                    Here's what your agent<br />will do.
                                </h2>
                                <p className="text-gray-500">
                                    We'll configure your agent based on your selection.
                                </p>
                            </div>

                            {/* Capabilities */}
                            <div className="mb-8">
                                <p className="text-xs font-mono tracking-widest text-gray-400 uppercase mb-4">
                                    Agent Capabilities
                                </p>
                                <div className="space-y-2">
                                    {selectedAgentType?.capabilities.map((cap, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center gap-3 p-4 border border-gray-100 rounded-lg"
                                        >
                                            <HiCheck className="w-4 h-4 text-gray-600 flex-shrink-0" />
                                            <span className="text-sm text-gray-700">{cap}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Website URL */}
                            <div className="mb-8">
                                <label className="block text-xs font-mono tracking-widest text-gray-400 uppercase mb-2">
                                    Your Website *
                                </label>
                                <input
                                    type="text"
                                    value={websiteUrl}
                                    onChange={(e) => setWebsiteUrl(e.target.value)}
                                    placeholder="https://www.yourcompany.com"
                                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300"
                                />
                                <p className="mt-2 text-xs text-gray-400">
                                    We'll use your website to train your agent with relevant information about your business.
                                </p>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    /* onClick={() => setCurrentStep(2)} */
                                    onClick={() => setCurrentStep(SKIP_AGENT_TYPE_STEP ? 1 : 2)}
                                    className="flex-1 py-3.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleBuildAgent}
                                    disabled={!websiteUrl.trim()}
                                    className="flex-1 py-3.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed bg-primary text-white hover:bg-secondary"
                                >
                                    Build my agent <span className="text-base">✨</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Building */}
                    {currentStep === 4 && (
                        <div className="text-center pt-12">
                            <div className="flex justify-center mb-6">
                                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                                    <AiOutlineLoading3Quarters className="w-7 h-7 text-gray-600 animate-spin" />
                                </div>
                            </div>

                            <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                {extracting ? 'Extracting website content.' : 'Building your agent.'}
                            </h2>
                            <p className="text-gray-500 mb-10">
                                {extracting
                                    ? 'Crawling and indexing your website. This may take a few minutes.'
                                    : 'This usually takes under 10 seconds.'}
                            </p>

                            {/* Progress */}
                            <div className="text-left">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-sm font-medium text-gray-700">Progress</p>
                                    <p className="text-sm font-mono text-gray-400">{buildProgress}%</p>
                                </div>
                                <div className="w-full h-1 bg-gray-100 rounded-full mb-6">
                                    <div
                                        className="h-1 bg-primary rounded-full transition-all duration-500"
                                        style={{ width: `${buildProgress}%` }}
                                    />
                                </div>

                                <div className="space-y-2">
                                    {BUILDING_STEPS.map((step, i) => (
                                        <div
                                            key={i}
                                            className={`flex items-center gap-3 p-4 rounded-lg transition-all ${
                                                i === buildingStepIndex && !extracting
                                                    ? 'bg-gray-50 border border-gray-200'
                                                    : i < buildingStepIndex || extracting
                                                    ? 'text-gray-400'
                                                    : ''
                                            }`}
                                        >
                                            {i < buildingStepIndex || extracting ? (
                                                <HiCheck className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                            ) : i === buildingStepIndex ? (
                                                <AiOutlineLoading3Quarters className="w-4 h-4 text-gray-600 animate-spin flex-shrink-0" />
                                            ) : (
                                                <div className="w-4 h-4 rounded-full bg-gray-200 flex-shrink-0" />
                                            )}
                                            <div>
                                                <p className={`text-sm ${i <= buildingStepIndex || extracting ? 'text-gray-700' : 'text-gray-400'}`}>
                                                    {step.label}
                                                </p>
                                                {i === buildingStepIndex && !extracting && (
                                                    <p className="text-xs text-gray-400 mt-0.5">{step.sublabel}</p>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Extraction step */}
                                    <div
                                        className={`flex items-center gap-3 p-4 rounded-lg transition-all ${
                                            extracting
                                                ? 'bg-gray-50 border border-gray-200'
                                                : 'text-gray-400'
                                        }`}
                                    >
                                        {extracting ? (
                                            <AiOutlineLoading3Quarters className="w-4 h-4 text-gray-600 animate-spin flex-shrink-0" />
                                        ) : (
                                            <div className="w-4 h-4 rounded-full bg-gray-200 flex-shrink-0" />
                                        )}
                                        <div>
                                            <p className={`text-sm ${extracting ? 'text-gray-700' : 'text-gray-400'}`}>
                                                Extracting website content
                                            </p>
                                            {extracting && (
                                                <p className="text-xs text-gray-400 mt-0.5">Crawling and indexing pages from your website</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <p className="mt-8 text-xs font-mono text-gray-400">
                                {extracting
                                    ? '▊ Indexing website pages...'
                                    : '▊ Deploying to 15 edge regions worldwide'}
                            </p>
                        </div>
                    )}

                    {/* Step 5: Ready */}
                    {currentStep === 5 && (
                        <div className="text-center">
                            <div className="flex justify-center mb-6">
                                <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center">
                                    <HiCheck className="w-8 h-8 text-white" />
                                </div>
                            </div>

                            <p className="text-xs font-mono tracking-widest text-gray-400 uppercase mb-3">
                                Agent Live
                            </p>
                            <h2 className="text-3xl font-bold text-gray-900 mb-3">
                                Your agent is ready.
                            </h2>
                            <p className="text-gray-500 mb-4">
                                <span className="font-medium text-gray-900">{agentDisplayName}</span> is deployed and
                                <br />ready to handle conversations.
                            </p>

                            {extractionFailed && (
                                <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
                                    <p className="text-sm text-yellow-800">
                                        Website extraction is still in progress. Your agent is ready to use, and knowledge will be available once indexing completes.
                                    </p>
                                </div>
                            )}

                            {/* Try Asking */}
                            <div className="mb-8">
                                <p className="text-xs font-mono tracking-widest text-gray-400 uppercase mb-4">
                                    Try Asking
                                </p>
                                <div className="space-y-2">
                                    {getSampleQuestions().map((q, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center gap-3 p-4 border border-gray-100 rounded-lg text-left"
                                        >
                                            <HiOutlineChatAlt2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                            <span className="text-sm text-gray-700">{q}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Next Steps */}
                            <div className="mb-8">
                                <p className="text-xs font-mono tracking-widest text-gray-400 uppercase mb-4">
                                    Next Steps
                                </p>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-3 p-4 border border-gray-100 rounded-lg text-left">
                                        <span className="text-lg">🔧</span>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">Integrate Tools</p>
                                            <p className="text-xs text-gray-500">Connect APIs, CRMs, and databases to supercharge your agent.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-4 border border-gray-100 rounded-lg text-left">
                                        <span className="text-lg">📱</span>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">Integrate Channels</p>
                                            <p className="text-xs text-gray-500">Deploy on WhatsApp, SMS, Web Chat, Slack, and more.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-4 border border-gray-100 rounded-lg text-left">
                                        <span className="text-lg">🚀</span>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">Explore & Test</p>
                                            <p className="text-xs text-gray-500">Test your agent, fine-tune responses, and go live.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <button
                                onClick={handleTestAgent}
                                className="w-full py-3.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 bg-primary text-white hover:bg-secondary transition-colors mb-3"
                            >
                                Test your agent →
                            </button>
                            <button
                                onClick={handleExplorePlatform}
                                className="w-full py-3.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Explore the platform
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingWizard);
